import { Component, inject, Inject, NgZone, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { HomecardsService } from "@core/AuthServices/homeCards/homecards.service";
import { CelebrationAnimationService } from "@core/celebration-animation.service";
import { IonicModule, NavParams } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { of, Subject, Subscription } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable, Orientation } from "src/app/app.constants";
import { GameHudService } from "src/app/pages/game-new/components/animation-system/game-hud.service";
import { GamesFeedSampleService } from "src/app/pages/games-feed-sample/games-feed-sample.service";
import { AppPaymentService } from "src/app/services/AppRootService/app-payment.service";
import { GameSessionService } from "src/app/services/game-session.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { HomeDataService } from "src/app/services/home-data.service";
import { OrientationService } from "src/app/services/orientation.service";

@Component({
  selector: "app-game-play",
  templateUrl: "./game-play.component.html",
  styleUrls: ["./game-play.component.scss"],
  animations: [bounceIn],
  providers: [NavParams],
  standalone: true,
  imports: [IonicModule],
})
export class GamePlayComponent implements OnInit, OnDestroy {
  private static readonly HUD_SHOW_ON_RETURN_KEY = "game_new_hud_show_on_return";
  private static readonly ALLOWED_GAME_PATHS = new Set(["game", "game-new", "social"]);
  private actRoute = inject(ActivatedRoute);
  private appPaymentService = inject(AppPaymentService)
  private gameHud = inject(GameHudService)
  url: SafeResourceUrl;
  translatedToastMsg: any;
  selectedLanguage: string = CommonVariable?.preferredLanguage;
  counter = 0;
  playGameStatus: boolean;
  preferredCurrency = CommonVariable.preferredCurrency;
  playSessionSubscription: Subscription;
  gameMiniSubscription: Subscription;
  gamePopupSubscription: Subscription;
  private destroy$ = new Subject<void>();
  userId: string;
  constructor(
    private orientationService: OrientationService,
    private sanitizer: DomSanitizer,
    private GTMSegment: GTMSegmentService,
    @Inject(MAT_DIALOG_DATA) public gameData,
    public translate: TranslateService,
    private authService: AuthService,
    private router: Router,
    private celebrationAnimationService: CelebrationAnimationService,
    public gamesFeedService: GamesFeedSampleService,
    private gameSessionService: GameSessionService,
    private homeDataService: HomeDataService,
    private dialogRef: MatDialogRef<GamePlayComponent>,
    private ngZone: NgZone,
    private homeCardsService: HomecardsService,
  ) {
    this.actRoute.queryParams.subscribe((params) => {
      this.appPaymentService.initiatePayment(params);
    })
    // Subscribe to selected language
    this.getPreferredLanguage();

    // Subscribe to constants message
    this.getConstantMessage();

    // GTM and Celebration logic
    this.GTMSegment.GTMPopupInitialization("GAmePlayPopup");
    this.celebrationAnimationService.setGamePlayStatus(true);

    // Subscribe to home cards
    this.getHomeCards();
    // Setup message listener for cross-origin communication
    this.setupMessageListener();
  }

  async ngOnInit() {
    await this.authService
      ?.getUid("GamePlayComponent")
      ?.then((uid) => {
        this.userId = uid;
      })
      .catch((error) => {
        return error;
      });
    try {
      if (this.gameData?.data?.game_play_session) {
        this.url = this.sanitizer.bypassSecurityTrustResourceUrl(this.gameData?.data?.play_url);
        const userId = this.userId;
        const gameSessionId = this.gameData?.data?.game_play_session;
        if (!userId || !gameSessionId) {
          return;
        }
        this.playSessionSubscription = this.gameSessionService.getGameSessionChanges(userId, gameSessionId).subscribe(
          (data: any) => {
            if (!this.gameData || !this.gameData?.data || !this.gameData?.data || !this.gameData?.data?.play_url) {
              return;
            }
            this.handleGameSessionData(data);
          },
          (err) => {
            return err;
          }
        );
      } else {
        try {
          this.url = this.sanitizer?.bypassSecurityTrustResourceUrl(this.gameData?.data);
        } catch (error) {
          // Log error but don't attempt direct iframe access which causes SecurityError
          console.error("Error setting up game iframe URL:", error);
          // Continue gracefully - iframe will load from URL in template
        }
      }
    } catch (e) {
      console.error("Error in ngOnInit for GamePlayComponent:", e);
    }
  }

  ngOnDestroy(): void {
    // Emit a value and complete the destroy$ subject to clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    
    // Remove message event listener to prevent memory leaks
    // This prevents lingering listeners that might cause errors
    window.removeEventListener('message', this.handleMessage);
    
    // Unsubscribe from game session changes
    if (this.playSessionSubscription) {
      this.playSessionSubscription.unsubscribe();
    }
  }

  // Set up a cross-origin message listener for iframe communication
  private setupMessageListener(): void {
    window.addEventListener('message', this.handleMessage);
  }

  // Handle messages from game iframe and payment iframe
  // Validates origin to ensure secure cross-origin communication
  private handleMessage = (event: MessageEvent): void => {
    // Verify the origin of the message - only accept from trusted game and payment domains
    const trustedOrigins = ['https://js.checkout.com', 'https://game.lottosocial.com'];
    const isTrustedOrigin = trustedOrigins.some(origin => event?.origin === origin);

    if (!isTrustedOrigin) {
      console.warn(`Ignoring message from untrusted origin: ${event?.origin}`);
      return;
    }

    try {
      // Process the data safely within NgZone to trigger change detection
      this.ngZone.run(() => {
        const data = event?.data;
        if (!data) {
          return;
        }

        // Handle payment-related events from checkout iframe
        if (event?.origin === 'https://js.checkout.com' && data?.type === 'payment-event') {
          console.log('Received payment event:', data);
          return;
        }

        // Handle game-related events from game iframe
        if (event?.origin === 'https://game.lottosocial.com') {
          this.handleGameMessage(data);
        }
      });
    } catch (error) {
      console.error('Error processing message from iframe:', error);
    }
  }

  // Handle specific game iframe messages
  private handleGameMessage(data: any): void {
    try {
      // Check for iframe close signals (game finished)
      if ((data && data?.iframe_close) || (data && data?.iframe_close == 1)) {
        console.log('Game iframe close event received');
        // This is handled by game session data, not by direct iframe access
      }
    } catch (error) {
      console.error('Error handling game message:', error);
    }
  }

  onLoadFunc() {
    // Iframe load event - do not attempt direct DOM access
    // All communication with iframe must use postMessage API
    // to avoid cross-origin SecurityError
  }

  private getPreferredLanguage(): void {
    this.authService?.selectedlanguage$?.pipe(takeUntil(this.destroy$)).subscribe(
      (l) => {
        this.selectedLanguage = l;
        this.translate.use(l ? l : CommonVariable?.preferredLanguage);
      },
      (err) => {
        console.error("Error in selectedlanguage$", err);
      }
    );
  }

  private getConstantMessage(): void {
    this.authService?.getConstantsMsg$?.pipe(takeUntil(this.destroy$)).subscribe((msg: Data) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
  }

  private getHomeCards(): void {
    this.homeDataService?.homeCardsVM$
      ?.pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error("Error in homeCardsVM$", err);
          return of(null);
        })
      )
      .subscribe((data: Data) => {
        if (data && data.new_member_checklist && data.new_member_checklist.content) {
          const checkListCard = data.new_member_checklist.content.checklist;
          if (Array.isArray(checkListCard) && checkListCard.length > 0) {
            const item = checkListCard.find((item: Data) => item.title === "Play a Game");
            if (item) {
              this.playGameStatus = item?.status;
            }
          }
        }
      });
  }

  private resolveGamePath(): string {
    const stored = localStorage.getItem("game_path");
    return GamePlayComponent.ALLOWED_GAME_PATHS.has(stored ?? "") ? stored! : "game";
  }

  private handleGameSessionData(data) {
    try {
      if ((data && data?.iframe_close) || (data && data?.iframe_close == 1)) {
        this.orientationService.lockOrientation(Orientation.Portrait);
        const gamePath = this.resolveGamePath();
        const celebration = this.celebrationAnimationService;
        const homeCards = this.homeCardsService;
        const gameHud = this.gameHud;
        const runPostCloseSideEffects = () => {
          celebration.setGamePlayStatus(false);
          celebration.trackCelebrations();
          homeCards.updateAddToHomeCardStatus({ event: "play_game", status: true });
          if (gameHud.animateFlag()) {
            void gameHud.startAnimationQueue();
          }
        };

        // game-new reads this flag on init to force challenge HUD visible after iframe close
        if (gamePath === "game-new") {
          localStorage.setItem(GamePlayComponent.HUD_SHOW_ON_RETURN_KEY, "1");
          // Schedule on root GameHudService so navigation/dialog destroy cannot cancel it
          this.gameHud.scheduleAfterGameNewReturn(runPostCloseSideEffects);
        } else {
          runPostCloseSideEffects();
        }
        // In-game promotion redirection logic
        const redirectionUrl = data?.redirect_url;
        if (
          (data?.close_reason == "FINISH_PROMOTION_OFFER" || data?.close_reason == "FINISH_JACKPOT_TRIGGER" || data?.close_reason == "FINISH_AUTO_TOPUP" || data?.close_reason == "FINISH_REFER_FRIEND") &&
          redirectionUrl &&
          typeof redirectionUrl == "string" &&
          redirectionUrl !== ""
        ) {
          this.gameSessionService.inGamePromotionalRedirection(redirectionUrl);
        } else {
          this.router.navigateByUrl(`/mainTabs/tabs/${gamePath}`);
        }
        if (data?.close_reason == "FINISH_SUGGESTION" || data?.close_reason == "FINISH_PROMO_BANNER" && data?.redirect_url && data?.redirect_url !== "") {
          this.gameSessionService.handleFinishSuggestionCase(data, this.dialogRef);
        } else {
          // This method has been used to display toast message after finished game and checklist steps complete
          this.gameSessionService.handleToastMessageCase(data, this.playGameStatus, this.dialogRef);
        }
      }
    } catch (error) {
      return error;
    }
  }
}
